import { isInverterCharger, oneWayDcConnections } from "../component-ports";
import { isAcOnlyComponent } from "../ac";
import { getDefinition } from "../catalog";
import { rvPanelStateOption } from "../catalog/rv-distribution";
import { matchesAcCircuitProfileDefinition } from "../catalog/ac-circuits";
import type { Category, ComponentDefinition } from "../types";

/** Per-component state in Simulate mode. With `role` omitted the engine picks the
 *  rail by terminal kind. */
export type SimulateStateOption = {
  id: string;
  label: string;
  ampMultiplier?: number;
  ampOverride?: number;
  role?: "draw" | "output";
  /**
   * Terminal pairs tied together under this option. **Replaces** the default
   * polarity grouping — only these pairs conduct, every other terminal floats.
   * `ampOverride: 0` still wins: an "off" position breaks every intra-link.
   */
  intraLinks?: ReadonlyArray<readonly [string, string]>;
};

export type SimulateStateSpec = {
  default: string;
  options: SimulateStateOption[];
};

/** Fallback for any component drawn as a switch symbol. "Off" uses the
 *  `ampOverride: 0` sentinel, which severs the walk in `cable-current.ts`. */
const DEFAULT_SIMULATE_STATES_BY_RENDER_VARIANT: Partial<
  Record<NonNullable<ComponentDefinition["renderVariant"]>, SimulateStateSpec>
> = {
  switch: {
    default: "on",
    options: [
      { id: "on", label: "On", ampMultiplier: 1 },
      { id: "off", label: "Off", ampOverride: 0 },
    ],
  },
};

/** Per-category fallback. A category absent here gets no toggle and is always-on —
 *  its static spec values flow through unchanged. */
const DEFAULT_SIMULATE_STATES_BY_CATEGORY: Partial<Record<Category, SimulateStateSpec>> = {
  solar: {
    default: "sunny",
    options: [
      { id: "sunny", label: "Sunny", ampMultiplier: 1, role: "output" },
      { id: "cloudy", label: "Cloudy", ampMultiplier: 0.25, role: "output" },
      { id: "off", label: "Off", ampOverride: 0 },
    ],
  },
  source: {
    default: "off",
    options: [
      { id: "on", label: "On", ampMultiplier: 1 },
      { id: "off", label: "Off", ampOverride: 0 },
    ],
  },
  engine: {
    default: "off",
    options: [
      { id: "starting", label: "Starting", role: "draw" },
      { id: "running", label: "Running", role: "output" },
      { id: "off", label: "Off", ampOverride: 0 },
    ],
  },
  // "Off" zeroes with `ampMultiplier: 0`, never the `ampOverride: 0` sentinel:
  // that severs the converter bridge in `graph.ts`, and a charger toggled off must
  // stay conducting so its output cable is still sized for the charge current.
  charger: {
    default: "off",
    options: [
      { id: "on", label: "On", ampMultiplier: 1, role: "output" },
      { id: "off", label: "Off", ampMultiplier: 0 },
    ],
  },
};

const INVERTER_CHARGER_STATES: SimulateStateSpec = {
  default: "off",
  options: [
    // Keep the existing "on" id compatible with saved simulation contexts.
    { id: "on", label: "Charging", ampMultiplier: 1, role: "output" },
    { id: "inverting", label: "Inverting", ampMultiplier: 1, role: "draw" },
    { id: "off", label: "Off", ampMultiplier: 0 },
  ],
};

/** Exact reviewed models, including customized copies retaining their catalog id.
 * Generic converters (including the legacy "XS 25" placeholder) do not inherit
 * a manufacturer's engine-detection behavior. */
export function hasEngineShutdownDetection(def: ComponentDefinition): boolean {
  return ["dcdc-victron-orion-xs-50", "dcdc-victron-orion-18",
    "dcdc-victron-orion-30", "dcdc-victron-orion-1224-10"].includes(def.id);
}

// Orion-Tr Smart isolated manual §5: detection defaults to enabled in charger
// mode. Keep the legacy On id, with an explicit disabled-detection scenario.
// https://www.victronenergy.com/media/pg/Orion-Tr_Smart_DC-DC_Charger_-_Isolated/en/automatic-engine-shutdown-detection.html
const ENGINE_DETECTION_STATES: SimulateStateSpec = {
  default: "on",
  options: [
    { id: "on", label: "Auto", ampMultiplier: 1, role: "output" },
    { id: "forced", label: "Forced", ampMultiplier: 1, role: "output" },
    { id: "off", label: "Off", ampMultiplier: 0 },
  ],
};

/** Contact selection only: nominal battery voltage cannot resolve these
 * products' sensing delays, control wiring or manual overrides. */
export function hasManualCombinerStates(def: ComponentDefinition): boolean {
  return ["dist-victron-cyrix-ct-120", "dist-victron-cyrix-ct-230", "dist-bs-7622-ml-acr"].includes(def.id);
}

/** Undefined means "always on": no toggle, static specs. */
export function getSimulateStateSpec(
  def: ComponentDefinition,
): SimulateStateSpec | undefined {
  if (def.simulateStates) return def.simulateStates;
  const catalogEntry = getDefinition(def.id);
  if (catalogEntry?.simulateStates) return catalogEntry.simulateStates;
  if (hasEngineShutdownDetection(def)) return ENGINE_DETECTION_STATES;
  if (def.renderVariant) {
    const byVariant = DEFAULT_SIMULATE_STATES_BY_RENDER_VARIANT[def.renderVariant];
    if (byVariant) return byVariant;
  }
  if (isInverterCharger(def)) return INVERTER_CHARGER_STATES;
  return DEFAULT_SIMULATE_STATES_BY_CATEGORY[def.category];
}

export function getDefaultStateId(def: ComponentDefinition): string | undefined {
  return getSimulateStateSpec(def)?.default;
}

export function getStateOption(
  def: ComponentDefinition,
  optionId: string | undefined,
): SimulateStateOption | undefined {
  // Remember branch positions while the generic AC panel's main is open.
  // Existing saved IDs stay valid and design sweeps keep the finite options.
  if (optionId?.startsWith("main-off:") && ["dist-ac-panel-120", "dist-ac-panel-230"].includes(def.id) &&
      matchesAcCircuitProfileDefinition(def)) {
    const spec = getSimulateStateSpec(def)!;
    const previous = spec.options.find(option => option.id === optionId.slice("main-off:".length) && option.id !== "main-off");
    const mainOff = spec.options.find(option => option.id === "main-off");
    if (previous && mainOff) return { ...mainOff, id: optionId };
  }
  const panelOption = rvPanelStateOption(def, optionId);
  if (panelOption) return panelOption;
  const spec = getSimulateStateSpec(def);
  if (!spec) return undefined;
  const id = optionId ?? spec.default;
  return spec.options.find((o) => o.id === id);
}

/** Honor declared AC pole/isolation maps and reviewed control devices even
 * outside simulation. Their independent power/control ports are not busbars. */
export function getStaticIntraLinkOption(def: ComponentDefinition): SimulateStateOption | undefined {
  return [
    "dist-ctek-smartpass-120s", "bms-victron-smart-12-200", "dist-bep-701-md",
    "dist-spdt-center-off", "dist-dpdt-center-off", "dist-momentary-spst",
    "dist-relay-spdt-12v", "dist-pv-combiner-3",
  ].includes(def.id) || isAcOnlyComponent(def)
    ? getStateOption(def, undefined) : undefined;
}

/** Loads have no state spec — their state is the `loadStates` boolean — so
 *  `getStateOption` alone never yields the `ampOverride: 0` the path-walks key on. */
const SYNTHETIC_OFF_LOAD: SimulateStateOption = {
  id: "off",
  label: "Off",
  ampOverride: 0,
};

/** Use this rather than `getStateOption` whenever a `SimulateContext` is in scope:
 *  it also honours the per-load on/off toggle. */
export function getEffectiveStateOption(
  def: ComponentDefinition,
  nodeId: string,
  simulate:
    | {
        componentStates: Record<string, string>;
        loadStates?: Record<string, { active: boolean }>;
      }
    | undefined,
): SimulateStateOption | undefined {
  if (!simulate) return undefined;
  if (
    def.category === "load" &&
    simulate.loadStates?.[nodeId]?.active === false
  ) {
    return SYNTHETIC_OFF_LOAD;
  }
  return getStateOption(def, simulate.componentStates[nodeId]);
}

/**
 * Terminals conducting to `entryTermId` under the active `intraLinks`. `undefined`
 * means no override (polarity grouping applies); an empty set means it floats.
 */
export function getActiveIntraLinkTargets(
  def: ComponentDefinition,
  nodeId: string,
  simulate:
    | {
        componentStates: Record<string, string>;
        loadStates?: Record<string, { active: boolean }>;
      }
    | undefined,
  entryTermId: string,
): Set<string> | undefined {
  // A semiconductor splitter cannot be walked as bidirectional bus copper.
  // Same-stud cables still connect; charging across the device is unsupported.
  if (oneWayDcConnections(def).some(pair => pair.includes(entryTermId))) return new Set([entryTermId]);
  const opt = simulate
    ? getEffectiveStateOption(def, nodeId, simulate)
    : getStaticIntraLinkOption(def);
  if (!opt?.intraLinks) return undefined;
  const out = new Set<string>();
  for (const [a, b] of opt.intraLinks) {
    if (a === entryTermId) out.add(b);
    else if (b === entryTermId) out.add(a);
  }
  return out;
}

export function applyStateToAmp(
  base: number | undefined,
  option: SimulateStateOption | undefined,
  roleFilter?: "draw" | "output",
): number | undefined {
  if (!option) return base;
  // An override ignores role — "off" zeroes draw and output at once.
  if (option.ampOverride != null) return option.ampOverride;
  // A rail-specific option suppresses reads of the other rail: an engine in
  // "Running" sources via ampOutput and contributes nothing on ampDraw.
  if (option.role) {
    if (!roleFilter || option.role !== roleFilter) return undefined;
  }
  if (option.ampMultiplier != null) {
    return base != null ? base * option.ampMultiplier : undefined;
  }
  return base;
}
